import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAiReadiness, triggerAiPrepare } from "../service/aiPrepare";

// Readiness states the backend can report. In-flight states keep the
// poller alive; terminal states (ready / failed / not_ready) stop it.
const IN_FLIGHT_STATES = new Set([
  "materializing",
  "queued",
  "extracting",
  "embedding",
]);

export const isReadinessInFlight = (state) => IN_FLIGHT_STATES.has(state);

/**
 * Track a single document's AI-chat readiness and expose the prepare
 * trigger.
 *
 * Contract (mirrors `usePollAnalysisJob`'s conventions):
 * - No-op when `documentId` is falsy (matter-level chat pane).
 * - Fetches once on mount / document switch. Polls every `intervalMs`
 *   (default 6s) ONLY while the state is in-flight (materializing /
 *   queued / extracting / embedding); the effect is keyed on the state
 *   so a transition into in-flight (e.g. right after `prepare()`)
 *   starts the interval and a terminal transition tears it down.
 * - Pauses while the browser tab is hidden; resumes on visibility.
 * - `prepare()` POSTs the trigger, then refreshes readiness so the UI
 *   flips to "Preparing…" without waiting a full poll tick.
 *
 * Returns `{ readiness, loading, prepareError, preparing, prepare }`.
 * `readiness` is the raw backend payload (state / detail / chunk_count /
 * has_markdown / prepare_enabled) or null before the first fetch.
 */
export default function useDocumentAiReadiness({
  documentId,
  intervalMs = 6000,
} = {}) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(Boolean(documentId));
  const [preparing, setPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState(null);
  // Generation counter guards against a slow response for document A
  // landing after the user switched the pane to document B.
  const generationRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!documentId) return null;
    const generation = generationRef.current;
    try {
      const data = await fetchAiReadiness(documentId);
      if (generationRef.current === generation) {
        setReadiness(data);
        setLoading(false);
      }
      return data;
    } catch (error) {
      if (generationRef.current === generation) {
        setLoading(false);
      }
      return null;
    }
  }, [documentId]);

  // Reset per-document state on document switch.
  useEffect(() => {
    generationRef.current += 1;
    setReadiness(null);
    setPrepareError(null);
    setPreparing(false);
    setLoading(Boolean(documentId));
  }, [documentId]);

  const state = readiness?.state || null;
  const shouldPoll = Boolean(documentId) && (state === null || isReadinessInFlight(state));

  useEffect(() => {
    if (!shouldPoll) return undefined;

    let cancelled = false;
    let intervalId = null;

    const poll = () => {
      if (!cancelled) refresh();
    };

    const start = () => {
      if (intervalId !== null) return;
      poll();
      intervalId = setInterval(poll, intervalMs);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const isVisible = () =>
      typeof document === "undefined" ||
      document.visibilityState !== "hidden";

    const handleVisibilityChange = () => {
      if (cancelled) return;
      if (isVisible()) {
        start();
      } else {
        stop();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (isVisible()) {
      start();
    }

    return () => {
      cancelled = true;
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      }
    };
  }, [shouldPoll, intervalMs, refresh]);

  const prepare = useCallback(async () => {
    if (!documentId || preparing) return;
    // Same stale-response guard as refresh(): if the user switches the
    // pane to another document while this POST is in flight, the old
    // document's response must not mark the NEW document ready (and
    // start stamping its document_id onto chat payloads) — Bugbot on
    // PR #234.
    const generation = generationRef.current;
    setPreparing(true);
    setPrepareError(null);
    try {
      const result = await triggerAiPrepare(documentId);
      if (generationRef.current !== generation) return;
      if (result?.readiness) {
        setReadiness((prev) => ({ ...(prev || {}), ...result.readiness }));
      }
      await refresh();
    } catch (error) {
      if (generationRef.current !== generation) return;
      const reason =
        error?.response?.data?.error ||
        "Could not start AI preparation. Please try again.";
      setPrepareError(reason);
    } finally {
      // Unconditional: on a generation change the reset effect already
      // cleared `preparing` for the new document; clearing again is a
      // no-op rather than a leak.
      setPreparing(false);
    }
  }, [documentId, preparing, refresh]);

  return { readiness, loading, preparing, prepareError, prepare, refresh };
}
